'use client';

import { Fragment } from 'react';
import { Col, Row } from 'react-bootstrap';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

import clsx from 'clsx';

import { ClassNameProps } from '@/types/common.types';

import { MovieCardSkelton } from '@/components/MovieCard/MovieCardSkelton';
import { Breakpoints } from '@/components/MovieList';
import styles from '@/components/MovieList/MovieList.module.scss';
import { MovieTheaterSkeltonCard } from '@/components/MovieTheaterCard/MovieTheaterSkeltonCard';
import { SectionHeading } from '@/components/SectionHeading';
import { TruncatedText } from '@/components/TruncatedText';
import { UnfinishedMovieCardSkelton } from '@/components/UnfinishedMovieCard/UnfinishedMovieCard';

interface MovieListSkeltonProps extends ClassNameProps {
  hasDescription?: boolean;
  shouldRemoveHeader?: boolean;
  type?: 'movie' | 'unfinished-movie' | 'movie-theater';
  breakpoints?: Breakpoints;
}

const defaultBreakpoints = {
  xs: 2,
  sm: 3,
  md: 4,
  lg: 5,
  xl: 6,
};

export const MovieListSkelton = (props: MovieListSkeltonProps) => {
  const {
    hasDescription,
    shouldRemoveHeader,
    type = 'movie',
    breakpoints = defaultBreakpoints,
    className,
  } = props;

  const renderCardSkelton = (type: MovieListSkeltonProps['type']) => {
    switch (type) {
      case 'movie':
        return <MovieCardSkelton />;
      case 'unfinished-movie':
        return <UnfinishedMovieCardSkelton />;
      case 'movie-theater':
        return <MovieTheaterSkeltonCard />;
      default:
        return <MovieCardSkelton />;
    }
  };

  return (
    <Fragment>
      {!shouldRemoveHeader && (
        <div className={clsx(styles.topPanel, className)}>
          <SectionHeading>
            <SkeletonTheme baseColor="#0F0F12" highlightColor="#47474A" width={240}>
              <Skeleton />
            </SkeletonTheme>
          </SectionHeading>
          {hasDescription && (
            <TruncatedText lines={2} className={styles.description}>
              <SkeletonTheme baseColor="#0F0F12" highlightColor="#47474A" width={280}>
                <Skeleton />
              </SkeletonTheme>
            </TruncatedText>
          )}
        </div>
      )}

      <Row as="ul" {...breakpoints} className="mb-3">
        {Array(30)
          .fill(null)
          .map((_, index) => (
            <Col key={index} as="li" className="mb-4">
              {renderCardSkelton(type)}
            </Col>
          ))}
      </Row>
    </Fragment>
  );
};
